import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Star, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoldenTriangle = () => {
  const [goldenTriangleItineraries, setGoldenTriangleItineraries] = useState([]);
  const [loading, setLoading] = useState(true);

  const whatsappNumber = '917827327243';
  const whatsappMessage = 'Hi! I want to enquire about the Golden Triangle tour.';

  useEffect(() => {
    const fetchItineraries = async () => {
      try {
        const response = await axios.get(`${API}/golden-triangle`);
        setGoldenTriangleItineraries(response.data);
      } catch (error) {
        console.error('Error fetching itineraries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItineraries();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: `linear-gradient(rgba(255, 254, 242, 0.8), rgba(255, 254, 242, 0.8)), url('https://images.unsplash.com/photo-1564507592333-c60657eea523?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHx0YWolMjBtYWhhbHxlbnwwfHx8fDE3NjU4ODI2NzB8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '120px 0',
          textAlign: 'center'
        }}
      >
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '24px' }}>Golden Triangle Tours</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto 32px' }}>
            Experience India's most iconic circuit - Delhi, Agra, and Jaipur. Discover magnificent monuments, rich history, and vibrant culture.
          </p>
          <a
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Plan My Golden Triangle Tour
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Why Golden Triangle */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid-two-column" style={{ gap: '80px' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>Why the Golden Triangle?</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                The Golden Triangle is India's most famous tourist circuit, connecting three historic cities that showcase the best of North India's heritage, architecture, and culture.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <MapPin size={24} color="var(--text-primary)" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Delhi</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Explore the contrast of Old and New Delhi, from Mughal monuments to colonial architecture
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Star size={24} color="var(--text-primary)" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Agra</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Witness the eternal symbol of love - the Taj Mahal, and the magnificent Agra Fort
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <MapPin size={24} color="var(--text-primary)" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Jaipur</h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Experience the Pink City's royal palaces, hilltop forts, and vibrant bazaars
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1587135941948-670b381f08ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHx0YWolMjBtYWhhbHxlbnwwfHx8fDE3NjU4ODI2NzB8MA&ixlib=rb-4.1.0&q=85"
                alt="Taj Mahal"
                style={{ width: '100%', height: '500px', objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tour Packages */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ marginBottom: '48px', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>Choose Your Journey</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Select from our carefully crafted itineraries
            </p>
          </div>
          <div className="grid-three-column">
            {goldenTriangleItineraries.map((itinerary) => (
              <div
                key={itinerary.itinerary_id}
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  padding: '32px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ marginBottom: 'auto' }}>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>{itinerary.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
                    {itinerary.duration}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {itinerary.highlights.map((highlight, idx) => (
                      <li key={idx} style={{ marginBottom: '12px' }}>
                        <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi! I want to book the ${itinerary.title}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ marginTop: '24px', width: '100%', textDecoration: 'none' }}
                >
                  Enquire on WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '48px' }}>Tour Inclusions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' }}>
            <div style={{ textAlign: 'center' }}>
              <Calendar size={32} color="var(--text-primary)" style={{ margin: '0 auto 16px' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px' }}>Accommodation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Comfortable hotels with breakfast</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <MapPin size={32} color="var(--text-primary)" style={{ margin: '0 auto 16px' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px' }}>Transport</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Private AC vehicle throughout</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Star size={32} color="var(--text-primary)" style={{ margin: '0 auto 16px' }} />
              <h3 className="heading-3" style={{ marginBottom: '8px' }}>Expert Guides</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Local guides at each destination</p>
            </div>
          </div>
        </div>
      </section>

      {/* Removed Enquiry Form - WhatsApp is primary contact method */}

      {/* Best For */}
      <section className="section-padding-small" style={{ background: 'var(--bg-primary)', textAlign: 'center' }}>
        <div className="container">
          <p className="body-large" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            The Golden Triangle tour is perfect for first-time visitors to India, foreign tourists, families, and anyone seeking to experience India's most iconic destinations in one journey.
          </p>
        </div>
      </section>
    </div>
  );
};

export default GoldenTriangle;